import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, onDocumentReady, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';
import { archivedMegaMenus } from './gymshark-archived-mega-data.js?v=2026091302';

const desktopMegaStates = new WeakMap();

/**
 * A custom element that manages the desktop header menu.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
// Shopify can morph an already-open header. Retain logical state across that handoff.
const desktopSession = { key: null, group: null, nested: null, pointer: null, focus: null };

class HeaderMenu extends Component {
  #activeItem = null;
  #events;
  #observer;
  #intentTimer;
  #origin;
  #previous;
  #keyboard = false;

  connectedCallback() {
    super.connectedCallback();
    this.#events = new AbortController();
    const options = { signal: this.#events.signal };
    this.#reconcile();
    this.#observer = new MutationObserver(this.#reconcile);
    this.#observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-active', 'aria-expanded'] });
    document.addEventListener('pointermove', this.#pointerMove, options);
    document.addEventListener('pointerleave', this.#windowLeave, options);
    document.addEventListener('visibilitychange', this.#visibility, options);
    document.addEventListener('focusin', this.#focusIn, options);
    window.addEventListener('resize', this.#resize, options);
    this.addEventListener('click', this.#click, options);
    document.addEventListener('keydown', this.#keydown, options);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#events?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#intentTimer);
  }

  get headerComponent() { return this.closest('header-component'); }

  #reconcile = () => {
    this.querySelectorAll('.gymshark-mega-menu[data-archived-mega-key]').forEach(setupDesktopMegaMenu);
    const current = findSubmenu(this.#activeItem);
    const state = desktopMegaStates.get(current);
    if (this.#activeItem?.isConnected && this.#activeItem.ariaExpanded === 'true' && current?.hasAttribute('data-active') && !current.inert && state?.activeGroup === desktopSession.group && state?.activeNested === desktopSession.nested) return;
    clearTimeout(this.#intentTimer);
    this.#activeItem = null;
    if (desktopSession.key && window.innerWidth >= 1024) {
      const submenu = [...this.querySelectorAll('.gymshark-mega-menu')].find(e => e.dataset.archivedMegaKey === desktopSession.key);
      const item = findMenuItem(submenu);
      if (item) {
        const { group, nested } = desktopSession;
        this.#open(item);
        if (group !== null) setDesktopGroup(submenu, group);
        if (nested !== null) setDesktopNested(submenu, group, nested);
        this.#save();
        const focus = desktopSession.focus;
        if (focus && !focus.node.isConnected) {
          const target = focus.selector ? submenu.querySelector(focus.selector) : item;
          this.#keyboard = true;
          target?.focus({ preventScroll: true });
        }
      }
    }
  };

  #save() {
    const submenu = findSubmenu(this.#activeItem);
    const state = desktopMegaStates.get(submenu);
    desktopSession.key = submenu?.dataset.archivedMegaKey || null;
    desktopSession.group = state?.activeGroup ?? null;
    desktopSession.nested = state?.activeNested ?? null;
  }

  #open(item) {
    if (window.innerWidth < 1024 || !item?.isConnected || !this.contains(item) || item === this.#activeItem) return;
    const previous = findSubmenu(this.#activeItem);
    if (this.#activeItem) this.#activeItem.ariaExpanded = 'false';
    if (previous) {
      delete previous.dataset.active;
      previous.inert = true;
      resetDesktopMegaMenu(previous);
    }
    this.#activeItem = item;
    const submenu = findSubmenu(item);
    if (!submenu) return this.#close();
    setupDesktopMegaMenu(submenu);
    resetDesktopMegaMenu(submenu);
    item.ariaExpanded = 'true';
    this.ariaExpanded = 'true';
    submenu.inert = false;
    submenu.dataset.active = '';
    const left = item.closest('.menu-list__list-item').getBoundingClientRect().left;
    submenu.style.setProperty('--gymshark-menu-offset-x', `${-left}px`);
    submenu.style.setProperty('--gymshark-menu-viewport-width', `${document.documentElement.clientWidth}px`);
    this.headerComponent.style.setProperty('--submenu-height', '0px');
    this.headerComponent.style.setProperty('--full-open-header-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    syncDesktopMegaMenuBackdrop(submenu);
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.#origin = desktopSession.pointer;
    this.#save();
  }

  #close(restoreFocus = false) {
    clearTimeout(this.#intentTimer);
    desktopSession.focus = null;
    const item = this.#activeItem;
    const submenu = findSubmenu(item);
    this.#activeItem = null;
    if (item) item.ariaExpanded = 'false';
    this.ariaExpanded = 'false';
    if (submenu) {
      if (submenu.contains(document.activeElement)) document.activeElement.blur();
      delete submenu.dataset.active;
      submenu.inert = true;
      resetDesktopMegaMenu(submenu);
    }
    this.#save();
    if (restoreFocus) item?.focus();
  }

  activate = event => {
    if (event.type.startsWith('pointer')) return;
    const item = findMenuItem(event.target);
    if (item) this.#open(item);
  };

  deactivate(event) {
    if (event?.type.startsWith('pointer')) return;
    this.#close();
  }

  #windowLeave = () => this.#close();
  #visibility = () => { if (document.hidden) this.#close(); };
  #focusIn = event => {
    if (this.contains(event.target)) {
      const submenu = event.target.closest('.gymshark-mega-menu');
      const group = event.target.closest('[data-mega-group]');
      const entry = event.target.closest('[data-mega-entry]');
      const third = event.target.closest('.gymshark-mega-menu__third-list');
      let selector = null;
      if (third) selector = `.gymshark-mega-menu__third-list > li:nth-child(${[...third.children].indexOf(event.target.closest('li')) + 1}) > a`;
      else if (entry) selector = `[data-mega-group="${group.dataset.megaGroup}"] [data-mega-entry="${entry.dataset.megaEntry}"] > :is(a,button)`;
      else if (group) selector = `[data-mega-group="${group.dataset.megaGroup}"] > :is(a,button)`;
      desktopSession.focus = submenu || event.target.matches('.menu-list__accessibility-button') ? { node: event.target, selector } : null;
    } else desktopSession.focus = null;
    if (this.#keyboard && this.#activeItem && !this.contains(event.target)) this.#close();
  };

  #resize = () => {
    this.#previous = null;
    clearTimeout(this.#intentTimer);
    setHeaderMenuStyle();
    if (window.innerWidth < 1024) return this.#close();
    const submenu = findSubmenu(this.#activeItem);
    if (!submenu) return;
    submenu.style.setProperty('--gymshark-menu-offset-x', `${-this.#activeItem.closest('.menu-list__list-item').getBoundingClientRect().left}px`);
    submenu.style.setProperty('--gymshark-menu-viewport-width', `${document.documentElement.clientWidth}px`);
    syncDesktopMegaMenuBackdrop(submenu);
  };

  #pointerMove = event => {
    if (event.pointerType === 'touch' || window.innerWidth < 1024 || !this.isConnected) return;
    const point = { x: event.clientX, y: event.clientY };
    const previous = this.#previous;
    this.#previous = point;
    desktopSession.pointer = point;
    if (previous && point.x === previous.x && point.y === previous.y) return;
    this.#keyboard = false;
    desktopSession.focus = null;
    const header = this.headerComponent;
    if (!header) return;
    const submenu = findSubmenu(this.#activeItem);
    const headerRect = header.getBoundingClientRect();
    const inHeader = pointInRect(point, headerRect);
    const columns = submenu ? [...submenu.querySelectorAll('.gymshark-mega-menu__column')].filter(e => e.getBoundingClientRect().width && getComputedStyle(e).display !== 'none') : [];
    const column = columns.find(e => pointInRect(point, e.getBoundingClientRect()));
    const primary = event.target instanceof Element ? event.target.closest('.menu-list__list-item') : null;
    if (inHeader && primary && this.contains(primary)) {
      clearTimeout(this.#intentTimer);
      const item = findMenuItem(primary);
      const first = columns[0]?.getBoundingClientRect();
      const headingIntoMenu = first && previous && this.#origin && point.y > previous.y && pointInTriangle(point, this.#origin, { x: first.left, y: first.top }, { x: first.right, y: Math.min(first.bottom, innerHeight) });
      if (!headingIntoMenu) {
        if (item) this.#open(item); else this.#close();
        this.#origin = point;
      } else if (item !== this.#activeItem) {
        this.#intentTimer = setTimeout(() => {
          if (item) this.#open(item); else this.#close();
          this.#origin = point;
        }, 300);
      }
      return;
    }
    if (!submenu) return;
    if (column) {
      clearTimeout(this.#intentTimer);
      const state = desktopMegaStates.get(submenu);
      const target = event.target instanceof Element ? event.target : null;
      const entry = target?.closest('[data-mega-entry]');
      const group = target?.closest('[data-mega-group]');
      if (entry && column.classList.contains('gymshark-mega-menu__sublist')) {
        const index = Number(entry.dataset.megaEntry);
        const third = columns.find(e => e.classList.contains('gymshark-mega-menu__third-list'));
        const towardChild = third && previous && previous.x >= column.getBoundingClientRect().left && point.x > previous.x && pointInTriangle(point, this.#origin || previous, { x: third.getBoundingClientRect().left, y: headerRect.bottom }, { x: third.getBoundingClientRect().left, y: innerHeight });
        if (towardChild && state.activeNested !== index) {
          this.#intentTimer = setTimeout(() => { setDesktopNested(submenu, state.activeGroup, index); this.#save(); }, 300);
        } else {
          setDesktopNested(submenu, state.activeGroup, index);
          this.#origin = point;
        }
      } else if (group && column.classList.contains('gymshark-mega-menu__groups')) {
        const index = Number(group.dataset.megaGroup);
        const child = columns.find(e => e.classList.contains('gymshark-mega-menu__sublist'));
        const towardChild = child && previous && point.x > previous.x && state.activeGroup !== index && pointInTriangle(point, this.#origin || previous, { x: child.getBoundingClientRect().left, y: headerRect.bottom }, { x: child.getBoundingClientRect().left, y: innerHeight });
        if (towardChild) {
          this.#intentTimer = setTimeout(() => { setDesktopGroup(submenu, state.menu.groups[index].items.length ? index : null); this.#save(); }, 300);
        } else {
          setDesktopGroup(submenu, state.menu.groups[index].items.length ? index : null);
          this.#origin = point;
        }
      }
      this.#save();
      return;
    }
    const first = columns[0]?.getBoundingClientRect();
    const towardPanel = first && previous && this.#origin && point.y >= previous.y && point.x < previous.x && pointInTriangle(point, this.#origin, { x: first.left, y: first.top }, { x: first.right, y: Math.min(first.bottom, innerHeight) });
    if (towardPanel) {
      clearTimeout(this.#intentTimer);
      this.#intentTimer = setTimeout(() => this.#close(), 300);
    } else this.#close();
  };

  #click = event => {
    clearTimeout(this.#intentTimer);
    if (!(event.target instanceof Element)) return;
    const primary = event.target.closest('.menu-list__accessibility-button');
    if (primary) {
      event.preventDefault();
      this.#keyboard = event.detail === 0;
      if (primary === this.#activeItem) this.#close(); else this.#open(primary);
      return;
    }
    const branch = event.target.closest('button[data-mega-branch]') || event.target.closest('.gymshark-mega-menu__arrow')?.parentElement.querySelector(':scope > button[data-mega-branch]');
    if (branch) {
      const submenu = branch.closest('.gymshark-mega-menu');
      const group = Number(branch.closest('[data-mega-group]').dataset.megaGroup);
      const entry = branch.closest('[data-mega-entry]');
      if (entry) setDesktopNested(submenu, group, Number(entry.dataset.megaEntry));
      else setDesktopGroup(submenu, group);
      this.#save();
      if (event.detail === 0) document.getElementById(branch.getAttribute('aria-controls'))?.querySelector('a,button')?.focus();
      return;
    }
    if (event.target.closest('.gymshark-mega-menu a,.gymshark-mega-menu__backdrop')) this.#close();
  };

  #keydown = event => {
    this.#keyboard = true;
    clearTimeout(this.#intentTimer);
    const submenu = findSubmenu(this.#activeItem);
    if (event.key === 'Escape' && submenu) {
      event.preventDefault();
      this.#close(true);
      return;
    }
    if (event.key === 'ArrowDown' && event.target.matches('.menu-list__accessibility-button')) {
      event.preventDefault();
      this.#open(event.target);
      findSubmenu(event.target)?.querySelector('a,button')?.focus();
      return;
    }
    if (!submenu || !submenu.contains(event.target)) return;
    const branch = event.target.closest('button[data-mega-branch]');
    if (event.key === 'ArrowRight' && branch) {
      event.preventDefault();
      branch.click();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      const column = event.target.closest('.gymshark-mega-menu__column');
      const control = column?.id && (submenu.querySelector(`[aria-expanded="true"][aria-controls="${column.id}"]`) || submenu.querySelector(`[aria-controls="${column.id}"]`));
      control?.focus();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const column = event.target.closest('.gymshark-mega-menu__column');
      const items = [...column.querySelectorAll(':scope > li > a,:scope > li > button')];
      const index = items.indexOf(event.target);
      if (index >= 0) { event.preventDefault(); items[(index + (event.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length]?.focus(); }
    }
  };
}

function pointInRect(point, rect) {
  return point.x >= rect.left && point.x < rect.right && point.y >= rect.top && point.y < Math.min(rect.bottom, innerHeight);
}

function pointInTriangle(p, a, b, c) {
  const cross = (p1, p2, p3) => (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
  const d = [cross(p, a, b), cross(p, b, c), cross(p, c, a)];
  return !(d.some(x => x < 0) && d.some(x => x > 0));
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/** @param {HTMLElement} submenu */
function syncDesktopMegaMenuBackdrop(submenu) {
  const headerComponent = submenu.closest('header-component');
  const backdrop = headerComponent?.nextElementSibling;
  if (!(backdrop instanceof HTMLElement) || !backdrop.classList.contains('gymshark-mega-menu-global-backdrop')) return;

  backdrop.style.setProperty(
    '--gymshark-mega-menu-backdrop-top',
    `${Math.max(0, Math.round(submenu.getBoundingClientRect().top))}px`
  );
}

function setupDesktopMegaMenu(submenu) {
  if (!(submenu instanceof HTMLElement)) return;
  const groupsContainer = submenu.querySelector('[data-gymshark-mega-groups]');
  const featuredContainer = submenu.querySelector('[data-gymshark-mega-featured]');
  const existing = desktopMegaStates.get(submenu);
  if (existing?.groupsContainer === groupsContainer && groupsContainer.childElementCount) return;
  const menu = archivedMegaMenus[submenu.dataset.archivedMegaKey];
  if (!menu || !groupsContainer || !featuredContainer) return;
  const state = { menu, groupsContainer, featuredContainer, activeGroup: null, activeNested: null, groups: [] };
  const id = submenu.querySelector('[id]')?.id || `mega-${submenu.dataset.archivedMegaKey}`;
  const fragment = document.createDocumentFragment();
  menu.groups.forEach((group, groupIndex) => {
    const item = document.createElement('li');
    item.className = 'gymshark-mega-menu__item';
    item.dataset.active = 'false';
    item.dataset.tier3Active = 'false';
    item.dataset.megaGroup = String(groupIndex);
    item.style.setProperty('--text-colour', group.color || '#000');
    const record = { item, entries: [] };
    state.groups.push(record);
    if (group.href && !group.items.length) {
      item.append(createDesktopLink(group));
      fragment.append(item);
      return;
    }
    const sublist = document.createElement('ul');
    sublist.className = 'gymshark-mega-menu__column gymshark-mega-menu__sublist list-unstyled';
    sublist.id = `${id}-group-${groupIndex}`;
    sublist.ariaLabel = group.label;
    const thirdList = document.createElement('ul');
    thirdList.className = 'gymshark-mega-menu__column gymshark-mega-menu__third-list list-unstyled';
    thirdList.id = `${id}-group-${groupIndex}-children`;
    thirdList.ariaLabel = `${group.label} submenu`;
    record.control = createDesktopBranch(group.label, sublist.id);
    record.sublist = sublist;
    record.thirdList = thirdList;
    item.append(record.control, createMegaArrow(true));
    group.items.forEach((entry, entryIndex) => {
      const entryItem = document.createElement('li');
      entryItem.className = 'gymshark-mega-menu__item';
      entryItem.dataset.active = 'false';
      entryItem.dataset.megaEntry = String(entryIndex);
      entryItem.style.setProperty('--text-colour', entry.color || '#000');
      const nested = { item: entryItem, children: entry.children.map(child => {
        const li = document.createElement('li');
        li.className = 'gymshark-mega-menu__item';
        li.style.setProperty('--text-colour', child.color || '#000');
        li.append(createDesktopLink(child));
        return li;
      }) };
      if (entry.children.length) {
        nested.control = createDesktopBranch(entry.label, thirdList.id);
        entryItem.append(nested.control, createMegaArrow(true));
      } else entryItem.append(createDesktopLink(entry));
      record.entries.push(nested);
      sublist.append(entryItem);
    });
    item.append(sublist, thirdList);
    fragment.append(item);
  });
  groupsContainer.replaceChildren(fragment);
  renderFeaturedTiles(featuredContainer, menu.featured, false);
  submenu.inert = !submenu.hasAttribute('data-active');
  desktopMegaStates.set(submenu, state);
  resetDesktopMegaMenu(submenu);
}

function createDesktopLink(data) {
  const link = document.createElement('a');
  link.className = 'gymshark-mega-menu__link';
  link.href = data.href;
  link.append(createMegaText(data.label));
  return link;
}

function createDesktopBranch(label, controls) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'gymshark-mega-menu__text';
  button.dataset.megaBranch = '';
  button.textContent = label;
  button.setAttribute('aria-controls', controls);
  button.ariaExpanded = 'false';
  button.style.cssText = 'font:inherit;border:0;outline:none;margin:0;appearance:none;background-color:transparent;text-align:left;cursor:inherit';
  return button;
}

function resetDesktopMegaMenu(submenu) {
  const state = desktopMegaStates.get(submenu);
  if (!state) return;
  state.activeGroup = undefined;
  setDesktopGroup(submenu, null);
}

function setDesktopGroup(submenu, groupIndex) {
  const state = desktopMegaStates.get(submenu);
  if (!state || state.activeGroup === groupIndex) return;
  state.activeGroup = groupIndex;
  state.activeNested = null;
  state.groups.forEach((group, index) => {
    const active = index === groupIndex;
    group.item.dataset.active = String(active);
    group.item.dataset.tier3Active = 'false';
    if (group.control) group.control.ariaExpanded = String(active);
    if (group.sublist) group.sublist.inert = !active;
    if (group.thirdList) { group.thirdList.inert = true; group.thirdList.replaceChildren(); }
    group.entries.forEach(entry => {
      entry.item.dataset.active = 'false';
      if (entry.control) entry.control.ariaExpanded = 'false';
    });
  });
  state.featuredContainer.hidden = !state.menu.featured.length || groupIndex !== null;
}

function setDesktopNested(submenu, groupIndex, entryIndex) {
  const state = desktopMegaStates.get(submenu);
  if (!state || state.activeGroup !== groupIndex || state.activeNested === entryIndex) return;
  const group = state.groups[groupIndex];
  if (!group?.thirdList) return;
  state.activeNested = entryIndex;
  group.entries.forEach((entry, index) => {
    entry.item.dataset.active = String(index === entryIndex);
    if (entry.control) entry.control.ariaExpanded = String(index === entryIndex);
  });
  const children = group.entries[entryIndex]?.children || [];
  group.item.dataset.tier3Active = String(children.length > 0);
  group.thirdList.inert = !children.length;
  group.thirdList.replaceChildren(...children);
}


/** @param {string} label */
function createMegaText(label) {
  const text = document.createElement('span');
  text.className = 'gymshark-mega-menu__text';
  text.textContent = label;
  return text;
}

/** @param {boolean} [hidden] */
function createMegaArrow(hidden = false) {
  const arrow = document.createElement('span');
  arrow.className = 'gymshark-mega-menu__arrow';
  const icon = document.createElement('i');
  icon.className = 'gymshark-icon icon-chevron-right';
  if (hidden) icon.ariaHidden = 'true';
  arrow.append(icon);
  return arrow;
}

/** @param {HTMLElement} container @param {{title: string, href: string, image: string, alt: string}[]} featured @param {boolean} mobile */
function renderFeaturedTiles(container, featured, mobile) {
  container.replaceChildren();
  if (!featured.length) {
    container.hidden = true;
    return;
  }

  const prefix = mobile ? 'gymshark-mobile-nav' : 'gymshark-mega-menu';
  const section = document.createElement('section');
  section.className = `${prefix}__featured-section`;
  section.ariaLabel = 'featured-tiles-carousel';
  const heading = document.createElement('h5');
  heading.className = `${prefix}__featured-heading`;
  heading.textContent = 'Featured';
  const tiles = document.createElement('div');
  tiles.className = `${prefix}__featured-tiles`;

  featured.forEach((tile) => {
    const link = document.createElement('a');
    link.href = tile.href;
    link.title = tile.title;
    const article = document.createElement('article');
    article.className = `${prefix}__featured-tile`;
    const image = document.createElement('img');
    image.src = tile.image;
    image.alt = tile.alt;
    image.loading = mobile ? 'lazy' : 'eager';
    image.decoding = 'async';
    const gradient = document.createElement('span');
    gradient.className = `${prefix}__featured-gradient`;
    const copy = document.createElement('span');
    copy.className = `${prefix}__featured-copy`;
    const title = document.createElement('span');
    title.textContent = tile.title;
    const cta = document.createElement('span');
    cta.className = `${prefix}__featured-cta`;
    cta.ariaHidden = 'true';
    const icon = document.createElement('i');
    icon.className = 'gymshark-icon icon-chevron-right';
    cta.append(icon);
    copy.append(title);
    article.append(image, gradient, copy, cta);
    link.append(article);
    tiles.append(link);
  });

  section.append(heading, tiles);
  container.append(section);
  container.hidden = false;
}

class GymsharkMobileNav extends HTMLElement {
  /** @type {boolean} */
  #initialized = false;

  /** @type {HTMLDetailsElement | null} */
  #details = null;

  /** @type {HTMLElement | null} */
  #lastFocusedElement = null;

  /** @type {string} */
  #bodyOverflow = '';

  /** @type {string} */
  #pageOverflow = '';

  /** @type {MutationObserver | null} */
  #hydrationObserver = null;

  connectedCallback() {
    if (this.#initialized) {
      hydrateMobileArchivedMegaMenus(this);
      return;
    }
    this.#initialized = true;
    const hydrateArchivedMenus = () => {
      const hydrated = hydrateMobileArchivedMegaMenus(this);
      if (hydrated) {
        this.#hydrationObserver?.disconnect();
        this.#hydrationObserver = null;
      }
    };
    this.#hydrationObserver = new MutationObserver(hydrateArchivedMenus);
    this.#hydrationObserver.observe(this, { childList: true, subtree: true });
    hydrateArchivedMenus();
    window.setTimeout(hydrateArchivedMenus, 0);
    onDocumentReady(hydrateArchivedMenus);
    this.#details = this.querySelector('.gymshark-mobile-nav__details');
    this.#details?.addEventListener('toggle', this.#toggleListener);
    this.addEventListener('click', this.#clickListener);
    this.addEventListener('keydown', this.#keydownListener);
    window.addEventListener('resize', this.#resizeListener);
  }

  disconnectedCallback() {
    this.#initialized = false;
    this.#hydrationObserver?.disconnect();
    this.#hydrationObserver = null;
    this.#details?.removeEventListener('toggle', this.#toggleListener);
    this.removeEventListener('click', this.#clickListener);
    this.removeEventListener('keydown', this.#keydownListener);
    window.removeEventListener('resize', this.#resizeListener);
    this.#unlockScroll();
  }

  initialize() {
    if (!this.#initialized) this.connectedCallback();
    hydrateMobileArchivedMegaMenus(this);
  }

  #toggleListener = () => {
    if (!this.#details) return;

    if (this.#details.open) {
      this.#lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.#lockScroll();
      requestAnimationFrame(() => this.querySelector('[data-gymshark-mobile-close]')?.focus());
      return;
    }

    this.#resetTiers();
    this.#unlockScroll();
    this.#lastFocusedElement?.focus();
    this.#lastFocusedElement = null;
  };

  #clickListener = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-gymshark-mobile-close]')) {
      this.#close();
      return;
    }

    const groupButton = event.target.closest('[data-gymshark-mobile-group]');
    if (groupButton instanceof HTMLButtonElement) {
      this.#openTierTwo(groupButton);
      return;
    }

    const thirdTierButton = event.target.closest('[data-gymshark-mobile-parent]');
    if (thirdTierButton instanceof HTMLButtonElement) {
      this.#openTierThree(thirdTierButton);
      return;
    }

    if (event.target.closest('[data-gymshark-mobile-back-two]')) {
      this.#closeTierTwo();
      return;
    }

    if (event.target.closest('[data-gymshark-mobile-back-three]')) {
      this.#closeTierThree();
      return;
    }

    const tab = event.target.closest('[data-gymshark-mobile-tab]');
    if (tab instanceof HTMLButtonElement) {
      this.#selectTab(tab, true);
      return;
    }

    if (event.target.closest('[data-gymshark-mobile-search]')) {
      this.#close(false);
      requestAnimationFrame(() => {
        const searchButton = document.querySelector('search-button .header-actions__action');
        if (searchButton instanceof HTMLButtonElement) searchButton.click();
      });
    }
  };

  #keydownListener = (event) => {
    if (!this.#details?.open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.#close();
      return;
    }

    const tabs = this.#tabs;
    const focusedTab = event.target instanceof Element ? event.target.closest('[data-gymshark-mobile-tab]') : null;
    if (focusedTab instanceof HTMLButtonElement && tabs.includes(focusedTab)) {
      const currentIndex = tabs.indexOf(focusedTab);
      let nextIndex = -1;

      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;

      if (nextIndex >= 0) {
        event.preventDefault();
        this.#selectTab(tabs[nextIndex], true);
      }
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = this.#focusableElements;
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  #resizeListener = debounce(() => {
    if (window.innerWidth >= 1024 && this.#details?.open) this.#close(false);
  }, 50);

  /** @param {HTMLButtonElement} tab */
  #selectTab(tab, focus) {
    this.#resetTiers();
    const tabs = this.#tabs;
    const panels = this.#panels;
    const selectedIndex = tabs.indexOf(tab);
    if (selectedIndex < 0) return;

    tabs.forEach((candidate, index) => {
      const selected = index === selectedIndex;
      candidate.classList.toggle('is-active', selected);
      candidate.ariaSelected = String(selected);
      candidate.tabIndex = selected ? 0 : -1;
    });

    panels.forEach((panel, index) => {
      const selected = index === selectedIndex;
      panel.hidden = !selected;
      panel.classList.toggle('is-active', selected);
      if (selected) {
        panel.style.animation = 'none';
        panel.offsetHeight;
        panel.style.removeProperty('animation');
      }
    });

    tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (focus) tab.focus();
  }

  #close(restoreFocus = true) {
    if (!this.#details) return;
    const lastFocusedElement = this.#lastFocusedElement;
    this.#resetTiers();
    this.#details.open = false;
    if (!restoreFocus) {
      this.#lastFocusedElement = null;
    } else {
      requestAnimationFrame(() => lastFocusedElement?.focus());
    }
  }

  #lockScroll() {
    const pageWrapper = document.querySelector('.page-wrapper');
    this.#bodyOverflow = document.body.style.overflow;
    this.#pageOverflow = pageWrapper instanceof HTMLElement ? pageWrapper.style.overflow : '';
    document.body.style.overflow = 'hidden';
    if (pageWrapper instanceof HTMLElement) pageWrapper.style.overflow = 'hidden';
  }

  #unlockScroll() {
    const pageWrapper = document.querySelector('.page-wrapper');
    document.body.style.overflow = this.#bodyOverflow;
    if (pageWrapper instanceof HTMLElement) pageWrapper.style.overflow = this.#pageOverflow;
  }

  /** @param {HTMLButtonElement} button */
  #openTierTwo(button) {
    const panel = button.closest('[data-gymshark-mobile-panel]');
    const key = panel?.getAttribute('data-archived-mega-key');
    const groupIndex = Number(button.dataset.gymsharkMobileGroup);
    const group = key ? archivedMegaMenus[key]?.groups[groupIndex] : null;
    const tierOne = this.querySelector('.gymshark-mobile-nav__tier-one');
    const tierTwo = this.querySelector('[data-gymshark-mobile-tier-two]');
    const title = this.querySelector('[data-gymshark-mobile-tier-two-title]');
    const list = this.querySelector('[data-gymshark-mobile-tier-two-links]');
    if (!group || !(tierOne instanceof HTMLElement) || !(tierTwo instanceof HTMLElement) ||
      !(title instanceof HTMLElement) || !(list instanceof HTMLElement)) return;

    title.textContent = group.label;
    list.replaceChildren();
    list.id = `navigation-subCategories-${slugify(group.label)}-list`;
    group.items.forEach((entry, index) => {
      const item = document.createElement('li');
      item.style.setProperty('--gymshark-mobile-item-delay', `${300 + 20 * index}ms`);
      if (entry.children.length) {
        const parent = document.createElement('button');
        parent.type = 'button';
        parent.className = 'gymshark-mobile-nav__tier-parent';
        parent.dataset.gymsharkMobileParent = String(index);
        parent.dataset.archivedMegaKey = key;
        parent.dataset.megaGroup = String(groupIndex);
        parent.style.setProperty('--custom-color', entry.color || '#000');
        const label = document.createElement('span');
        label.textContent = entry.label;
        const icon = document.createElement('i');
        icon.className = 'gymshark-icon icon-chevron-right';
        icon.ariaHidden = 'true';
        parent.append(label, icon);
        item.append(parent);
      } else {
        item.className = 'gymshark-mobile-nav__tier-link-item';
        item.style.setProperty('--custom-color', entry.color || '#000');
        const link = document.createElement('a');
        link.href = entry.href;
        link.textContent = entry.label;
        item.append(link);
      }
      list.append(item);
    });

    tierOne.classList.add('is-hidden');
    tierOne.ariaHidden = 'true';
    tierTwo.classList.add('is-show');
    tierTwo.classList.remove('is-behind');
    tierTwo.ariaHidden = 'false';
    tierTwo.dataset.archivedMegaKey = key;
    tierTwo.dataset.megaGroup = String(groupIndex);
  }

  /** @param {HTMLButtonElement} button */
  #openTierThree(button) {
    const key = button.dataset.archivedMegaKey;
    const groupIndex = Number(button.dataset.megaGroup);
    const entryIndex = Number(button.dataset.gymsharkMobileParent);
    const entry = key ? archivedMegaMenus[key]?.groups[groupIndex]?.items[entryIndex] : null;
    const tierTwo = this.querySelector('[data-gymshark-mobile-tier-two]');
    const tierThree = this.querySelector('[data-gymshark-mobile-tier-three]');
    const title = this.querySelector('[data-gymshark-mobile-tier-three-title]');
    const list = this.querySelector('[data-gymshark-mobile-tier-three-links]');
    if (!entry?.children.length || !(tierTwo instanceof HTMLElement) || !(tierThree instanceof HTMLElement) ||
      !(title instanceof HTMLElement) || !(list instanceof HTMLElement)) return;

    title.textContent = entry.label;
    list.replaceChildren();
    entry.children.forEach((child, index) => {
      const item = document.createElement('li');
      item.className = 'gymshark-mobile-nav__tier-link-item';
      item.style.setProperty('--gymshark-mobile-item-delay', `${300 + 20 * index}ms`);
      item.style.setProperty('--custom-color', child.color || '#000');
      const link = document.createElement('a');
      link.href = child.href;
      link.textContent = child.label;
      item.append(link);
      list.append(item);
    });

    tierTwo.classList.add('is-behind');
    tierThree.classList.add('is-show');
    tierThree.ariaHidden = 'false';
  }

  #closeTierTwo() {
    const tierOne = this.querySelector('.gymshark-mobile-nav__tier-one');
    const tierTwo = this.querySelector('[data-gymshark-mobile-tier-two]');
    if (!(tierOne instanceof HTMLElement) || !(tierTwo instanceof HTMLElement)) return;
    tierOne.classList.remove('is-hidden');
    tierOne.ariaHidden = 'false';
    tierTwo.classList.remove('is-show');
    tierTwo.ariaHidden = 'true';
    clearTierAfterTransition(tierTwo, () => {
      this.querySelector('[data-gymshark-mobile-tier-two-links]')?.replaceChildren();
    });
  }

  #closeTierThree() {
    const tierTwo = this.querySelector('[data-gymshark-mobile-tier-two]');
    const tierThree = this.querySelector('[data-gymshark-mobile-tier-three]');
    if (!(tierTwo instanceof HTMLElement) || !(tierThree instanceof HTMLElement)) return;
    tierTwo.classList.remove('is-behind');
    tierThree.classList.remove('is-show');
    tierThree.ariaHidden = 'true';
    clearTierAfterTransition(tierThree, () => {
      this.querySelector('[data-gymshark-mobile-tier-three-links]')?.replaceChildren();
    });
  }

  #resetTiers() {
    const tierOne = this.querySelector('.gymshark-mobile-nav__tier-one');
    const tierTwo = this.querySelector('[data-gymshark-mobile-tier-two]');
    const tierThree = this.querySelector('[data-gymshark-mobile-tier-three]');
    tierOne?.classList.remove('is-hidden');
    tierTwo?.classList.remove('is-show', 'is-behind');
    tierThree?.classList.remove('is-show');
    if (tierOne instanceof HTMLElement) tierOne.ariaHidden = 'false';
    if (tierTwo instanceof HTMLElement) tierTwo.ariaHidden = 'true';
    if (tierThree instanceof HTMLElement) tierThree.ariaHidden = 'true';
    this.querySelector('[data-gymshark-mobile-tier-two-links]')?.replaceChildren();
    this.querySelector('[data-gymshark-mobile-tier-three-links]')?.replaceChildren();
  }

  get #tabs() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[data-gymshark-mobile-tab]')]);
  }

  get #panels() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-gymshark-mobile-panel]')]);
  }

  get #focusableElements() {
    const dialog = this.querySelector('.gymshark-mobile-nav__dialog');
    if (!dialog) return [];

    return /** @type {HTMLElement[]} */ (
      [...dialog.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')].filter(
        (element) => element.offsetParent !== null
      )
    );
  }
}

/** @param {HTMLElement} root @returns {boolean} */
function hydrateMobileArchivedMegaMenus(root) {
  const panels = [...root.querySelectorAll('[data-gymshark-mobile-panel][data-archived-mega-key]')];
  panels.forEach((panel) => {
    if (!(panel instanceof HTMLElement) || panel.dataset.megaHydrated === 'true') return;
    const menu = archivedMegaMenus[panel.dataset.archivedMegaKey];
    const groups = panel.querySelector('[data-gymshark-mobile-groups]');
    const featured = panel.querySelector('[data-gymshark-mobile-featured]');
    if (!menu || !(groups instanceof HTMLElement) || !(featured instanceof HTMLElement)) return;

    renderFeaturedTiles(featured, menu.featured, true);
    menu.groups.forEach((group, groupIndex) => {
      const item = document.createElement('li');
      item.className = 'gymshark-mobile-nav__group-item';
      const heading = document.createElement('h5');

      if (group.href && !group.items.length) {
        const link = document.createElement('a');
        link.className = 'gymshark-mobile-nav__group-link';
        link.href = group.href;
        link.style.setProperty('--custom-color', group.color || '#000');
        const label = document.createElement('span');
        label.textContent = group.label;
        link.append(label);
        heading.append(link);
        item.append(heading);
        groups.append(item);
        return;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'gymshark-mobile-nav__group-button';
      button.dataset.gymsharkMobileGroup = String(groupIndex);
      button.setAttribute('aria-controls', `navigation-subCategories-${slugify(group.label)}-list`);
      button.style.setProperty('--custom-color', group.color || '#000');
      const label = document.createElement('span');
      label.textContent = group.label;
      const icon = document.createElement('i');
      icon.className = 'gymshark-icon icon-chevron-right';
      icon.ariaHidden = 'true';
      button.append(label, icon);
      heading.append(button);
      item.append(heading);
      groups.append(item);
    });
    panel.dataset.megaHydrated = 'true';
  });

  return panels.length === 4 && panels.every((panel) => panel.getAttribute('data-mega-hydrated') === 'true');
}

/** @param {HTMLElement} tier @param {() => void} callback */
function clearTierAfterTransition(tier, callback) {
  let completed = false;
  const finish = (event) => {
    if (completed || (event && event.target !== tier) || (event && event.propertyName !== 'right')) return;
    completed = true;
    tier.removeEventListener('transitionend', finish);
    callback();
  };
  tier.addEventListener('transitionend', finish);
  window.setTimeout(() => finish(), 350);
}

/** @param {string} value */
function slugify(value) {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

if (!customElements.get('gymshark-mobile-nav')) {
  customElements.define('gymshark-mobile-nav', GymsharkMobileNav);
}

function initializeMobileArchivedMenus() {
  document.querySelectorAll('gymshark-mobile-nav').forEach((root) => {
    if (root instanceof GymsharkMobileNav) root.initialize();
  });
}

onDocumentReady(() => {
  initializeMobileArchivedMenus();
  window.setTimeout(initializeMobileArchivedMenus, 0);
  window.setTimeout(initializeMobileArchivedMenus, 250);
  window.setTimeout(initializeMobileArchivedMenus, 1000);
});

document.addEventListener('shopify:section:load', initializeMobileArchivedMenus);

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  if (element.matches('[ref="menuitem"]')) return /** @type {HTMLElement} */ (element);

  const item = element.closest('.menu-list__list-item')?.querySelector('[ref="menuitem"]');
  return item instanceof HTMLElement ? item : null;
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.closest('.menu-list__list-item')?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
